import * as basket from "../eaiws/basket";
import * as project from "../eaiws/project";
import * as session from "../eaiws/session";
import { GroupedMap, ListenableEvent } from "../utils";
import { Lock } from "../utils/async";
import { contains } from "../utils/array";
export class ProjectInfo {
    get session() {
        return (this.mSession);
    }
    constructor(pSession) {
        this.eventSettingsChanged = new ListenableEvent(this);
        this.eventProjectDataChanged = new ListenableEvent(this);
        this.eventAppDataChanged = new ListenableEvent(this);
        this.eventAddressDataChanged = new ListenableEvent(this);
        this.eventContactDataChanged = new ListenableEvent(this);
        this.eventEffectiveProjectLanguagesChanged = new ListenableEvent(this);
        this.eventDefaultPriceDateChanged = new ListenableEvent(this);
        this.onLanguageChanged = () => {
            this.eventEffectiveProjectLanguagesChanged.trigger();
        };
        this.mSession = pSession;
        this.mSettings = null;
        this.mSettingsLock = new Lock();
        this.mProjectData = null;
        this.mProjectDataLock = new Lock();
        this.mAddressData = new Map();
        this.mAddressDataLock = new Lock();
        this.mContactData = null;
        this.mContactDataLock = new Lock();
        this.mBasketConfig = null;
        this.mBasketConfigLock = new Lock();
        this.mAppData = new GroupedMap();
        this.mAppDataLock = new Lock();
        this.mSession.eventSessionLocaleChanged.addListener(this.onLanguageChanged);
        this.mSession.eventBasketLanguagesChanged.addListener(this.onLanguageChanged);
    }
    /**
     * @obsolete
     * Should only be used for backwards compatibility.
     */
    async getSettings() {
        const tLockContext = await this.mSettingsLock.acquireLock();
        try {
            if (this.mSettings == null) {
                const tSettings = await this.mSession.session.getProjectSettings();
                this.mSettings = tSettings != null ? tSettings : new session.ProjectSettings();
            }
            return (this.mSettings);
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * This function can be used to change parts of the project settings. Properties which are undefined will
     * be ignored and not modified. Only properties which should be changed should have a value to reduce server load.
     * See EAIWS API documentation for further information.
     * @obsolete
     * Should only be used for backwards compatibility.
     */
    async changeSettings(pSettings) {
        const tLockContext = await this.mSettingsLock.acquireLock();
        try {
            await this.mSession.session.setProjectSettings(pSettings);
            //settings and project data share some fields in the metainf.xml of the OBK
            this.notifyProjectDataChanged();
            this.notifySettingsChanged();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Returns the project data of the current session.
     * Note: Project texts is returned in `JSON` format.
     */
    async getProjectData() {
        const tLockContext = await this.mProjectDataLock.acquireLock();
        const tAddrLockContext = await this.mAddressDataLock.acquireLock(); //also lock addresses because we also fill the cache here
        try {
            if (this.mProjectData == null) {
                const tProjectData = await this.mSession.project.getProjectData("JSON");
                this.mProjectData = tProjectData != null ? tProjectData : new project.ProjectData();
                //add addresses to cache
                if (this.mProjectData.addresses != null) {
                    for (const tAddr of this.mProjectData.addresses) {
                        this.mAddressData.set(tAddr.addressType, tAddr);
                    }
                }
            }
            return (this.mProjectData);
        }
        finally {
            tLockContext.unlock();
            tAddrLockContext.unlock();
        }
    }
    /**
     * This function can be used to change parts of the project data. Properties which are undefined will
     * be ignored and not modified. Only properties which should be changed should have a value to reduce server load.
     * See EAIWS API documentation for further information.
     */
    async changeProjectData(pData) {
        const tLockContext = await this.mProjectDataLock.acquireLock();
        try {
            await this.mSession.project.setProjectData(pData);
            //settings and project data share some fields in the metainf.xml of the OBK
            this.notifyProjectDataChanged();
            this.notifySettingsChanged();
            if (pData.languages != null) {
                this.onLanguageChanged();
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Can be used to trigger an update of the cached project data. Calling this function is only necessary if
     * the project data was modiefed without using the provided functions of this class.
     */
    notifyProjectDataChanged() {
        this.mProjectData = null;
        this.eventProjectDataChanged.trigger();
    }
    async getAppData(pAppKey, pDataPaths) {
        const tLockContext = await this.mAppDataLock.acquireLock();
        try {
            const tResult = new Map();
            //get cached data
            const tUnknownData = new Array();
            for (const tPath of pDataPaths) {
                const tValue = this.mAppData.getData(pAppKey, tPath);
                if (tValue !== undefined)
                    tResult.set(tPath, tValue);
                else
                    tUnknownData.push(tPath);
            }
            //get remaining data from EAIWS and add them to cache
            if (tUnknownData.length > 0) {
                const tValues = await this.mSession.session.getProjectAppData(pAppKey, tUnknownData);
                for (let i = 0; i < tUnknownData.length; ++i) {
                    tResult.set(tUnknownData[i], tValues[i]);
                    this.mAppData.setData(pAppKey, tUnknownData[i], tValues[i]);
                }
            }
            return (tResult);
        }
        finally {
            tLockContext.unlock();
        }
    }
    async setAppData(pAppKey, pData) {
        const tLockContext = await this.mAppDataLock.acquireLock();
        try {
            if (pData.size === 0)
                return;
            const tEaiwsData = new Array();
            pData.forEach((pValue, pKey) => {
                tEaiwsData.push(pKey + "=" + (pValue == null ? "" : pValue));
            });
            await this.mSession.session.setProjectAppData(pAppKey, tEaiwsData);
            //update cache
            pData.forEach((pValue, pKey) => {
                this.mAppData.setData(pAppKey, pKey, pValue);
            });
            this.eventAppDataChanged.trigger();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Retrieves the address data with the given type. Null will be returned if the address does not exist.
     */
    async getAddressData(pType) {
        await this.fetchAddressData([pType]);
        //get from cache
        const tAdr = this.mAddressData.get(pType);
        return (tAdr != null ? tAdr : null);
    }
    /**
     * Can be used to fetch multiple address data at the same time from the server.
     * Afterwards `getAddressData()` can be used to retrieve the cached address.
     */
    async fetchAddressData(pTypes) {
        const tLockContext = await this.mAddressDataLock.acquireLock();
        try {
            let tUnknownTypes;
            //check cache
            for (const tType of pTypes) {
                const tAdr = this.mAddressData.get(tType);
                if (tAdr === undefined) {
                    if (tUnknownTypes == null) {
                        tUnknownTypes = new Set();
                    }
                    tUnknownTypes.add(tType);
                }
            }
            //fetch addresses
            if (tUnknownTypes != null && tUnknownTypes.size > 0) {
                const tAdrArray = await this.mSession.project.getAddressData(Array.from(tUnknownTypes));
                //update cache
                for (const tAdr of tAdrArray) {
                    this.mAddressData.set(tAdr.addressType, tAdr);
                    tUnknownTypes.delete(tAdr.addressType);
                }
                //add remaining types as null to cache
                tUnknownTypes.forEach((pType) => this.mAddressData.set(pType, null));
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Removes/deletes one or more addresses.
     */
    async removeAddressData(pTypes) {
        const tLockContext = await this.mAddressDataLock.acquireLock();
        try {
            const tRemovedTypes = await this.mSession.project.removeAddressData(pTypes);
            //update cache
            for (const tType of tRemovedTypes) {
                this.mAddressData.delete(tType);
            }
            //trigger event
            for (const tType of tRemovedTypes) {
                this.eventAddressDataChanged.trigger(tType);
            }
            if (tRemovedTypes.length > 0) {
                this.notifyProjectDataChanged();
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * This function can be used to change parts of an address or add a new one. Properties which are undefined will
     * be ignored and not modified. Only properties which should be changed should have a value to reduce server load.
     * See EAIWS API documentation for further information.
     */
    async changeOrAddAddressData(pAddressData) {
        const tLockContext = await this.mAddressDataLock.acquireLock();
        try {
            await this.mSession.project.setAddressData(pAddressData);
            //remove from cache and trigger event
            this.mAddressData.delete(pAddressData.addressType);
            this.eventAddressDataChanged.trigger(pAddressData.addressType);
            //if contacts where changed trigger contact changed event
            if (pAddressData.contacts != null) {
                this.notifyContactDataChanged(pAddressData.contacts.map((c) => c.id), pAddressData.addressType);
            }
            this.notifyProjectDataChanged();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Retrieves the list of all contacts.
     */
    async getContactData() {
        const tLockContext = await this.mContactDataLock.acquireLock();
        try {
            if (this.mContactData == null) {
                this.mContactData = await this.mSession.project.getContactData();
            }
            return (this.mContactData);
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * This function can be used to change parts of an contact or add a new one. Properties which are undefined will
     * be ignored and not modified. Only properties which should be changed should have a value to reduce server load.
     * See EAIWS API documentation for further information.
     */
    async changeOrAddContactData(pContactData) {
        const tLockContext = await this.mContactDataLock.acquireLock();
        try {
            await this.mSession.project.setContactData(pContactData);
            this.notifyContactDataChanged([pContactData.id]);
            this.notifyProjectDataChanged();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Removes/deletes one or more contacts.
     */
    async removeContactData(pContactDataIds) {
        const tLockContext = await this.mContactDataLock.acquireLock();
        try {
            const tRemovedIds = await this.mSession.project.removeContactData(undefined, pContactDataIds);
            this.notifyContactDataChanged(tRemovedIds);
            this.notifyProjectDataChanged();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Retrieves the current default price date of the project.
     */
    async getDefaultPriceDate() {
        const tLockContext = await this.mBasketConfigLock.acquireLock();
        try {
            if (this.mBasketConfig == null) {
                const tConf = await this.mSession.basket.getBasketConfig();
                this.mBasketConfig = tConf !== null && tConf !== void 0 ? tConf : new basket.BasketConfig();
            }
            return (this.mBasketConfig.defaultPriceDate);
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Changes the default price date of the project.
     */
    async setDefaultPriceDate(pDate) {
        const tLockContext = await this.mBasketConfigLock.acquireLock();
        try {
            const tConf = new basket.BasketConfig();
            tConf.defaultPriceDate = pDate;
            await this.mSession.basket.changeBasketConfig(tConf);
            this.mBasketConfig = null;
            this.eventDefaultPriceDateChanged.trigger();
        }
        finally {
            tLockContext.unlock();
        }
    }
    /**
     * Resets this instance and clears internal caches.
     */
    clear() {
        this.mSettings = null;
        this.mProjectData = null;
        this.mAddressData = new Map();
        this.mContactData = null;
        this.mBasketConfig = null;
        this.mAppData.clear();
    }
    /**
     * Returns the currently effective project languages taking into account the different fallbacks.
     */
    async getEffectiveProjectLanguages() {
        const tData = await this.getProjectData();
        if (tData.languages != null && tData.languages.length > 0) {
            return (tData.languages);
        }
        //get languge from locale
        const tSessionLocale = await this.mSession.session.getLocale();
        const tSessionLang = tSessionLocale.substr(0, 2); //en_EN -> en
        //fallback to basket languages + session language
        const tBskLang = await this.mSession.basket.getLanguages();
        if (tBskLang.length > 0) {
            return ([...tBskLang, tSessionLang]);
        }
        //fallback to session language
        return ([tSessionLang]);
    }
    notifyContactDataChanged(pContactIds, pSourceAddress) {
        if (pContactIds.length === 0)
            return;
        //trigger address changed event for addresses which use these contacts
        this.mAddressData.forEach((pAddress) => {
            if (pAddress == null || pAddress.contacts == null || pAddress.addressType === pSourceAddress)
                return;
            for (const tAdrContact of pAddress.contacts) {
                if (contains(pContactIds, tAdrContact.id)) {
                    this.eventAddressDataChanged.trigger(pAddress.addressType);
                    return;
                }
            }
        });
        //reset cache and trigger contacts changed event
        this.mContactData = null;
        this.eventContactDataChanged.trigger();
    }
    notifySettingsChanged() {
        this.mSettings = null;
        this.eventSettingsChanged.trigger();
    }
}
